import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Row, Col } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type CheckboxFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { useDispatch, useSelector } from 'react-redux'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import {
  getCompanyDetailsFields,
  getBusinessAddressFields,
  getOfficeAddressFields
} from '../../../utils/fieldConfigs.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { FundingRoundsTable } from '../../tables/FundingRoundsTable/index.tsx'
import { ShortDescriptionSection } from './ShortDescriptionSection.tsx'
import { GeneralSection } from './GeneralSection.tsx'
import { accountHasPortfolioRole } from '../../../utils/accountRoles.ts'
import { buildAccountRoleOptions } from '../../../../../core/utils/accountRoleOptions.ts'

export function DetailsTab({
  currentCompany,
  formData,
  handleFieldChange,
  onPatchSuccess,
  recordId
}: {
  currentCompany?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  recordId?: string | null
}) {
  const { getLookupOptions } = useLookups()
  const dispatch = useDispatch()

  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const investorClassifications = useSelector((state: any) => state.globalConfigs?.investor_classifications || [])
  const shareRegisters = useSelector((state: any) => state.globalConfigs?.share_registers || [])
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getInvestorClassificationOptions = useCallback(() => {
    return investorClassifications.map((classification: any) => ({
      value: classification.id,
      label: classification.label
    }))
  }, [investorClassifications])

  const getShareRegisterOptions = useCallback(() => {
    return shareRegisters.map((register: any) => ({
      value: register.id,
      label: register.label
    }))
  }, [shareRegisters])

  const data = currentCompany || formData || {}

  // Funding Rounds is portfolio-only — hidden for accounts without a
  // Portfolio Company / Exited / Dead role (matches the other portfolio sections).
  const showPortfolioSections = accountHasPortfolioRole(currentCompany)

  const companyId =
    recordId || currentCompany?.[AccountRecordFields.accounts__id] || data[AccountRecordFields.accounts__id] || data.id

  const initialSameAsBusinessAddress =
    data[AccountRecordFields.accounts__same_as_business_address] !== undefined
      ? data[AccountRecordFields.accounts__same_as_business_address]
      : true

  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(initialSameAsBusinessAddress)

  useEffect(() => {
    const newValue =
      data[AccountRecordFields.accounts__same_as_business_address] !== undefined
        ? data[AccountRecordFields.accounts__same_as_business_address]
        : true
    setSameAsBusinessAddress(newValue)
  }, [data[AccountRecordFields.accounts__same_as_business_address]])

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(
        updateCompanyRecord({
          companyId: patchRecordId,
          updates
        })
      )
    }
  }

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      },
      getRoleOptions,
      getInvestorClassificationOptions,
      getShareRegisterOptions,
      countriesData
    }),
    [getLookupOptions, getRoleOptions, getInvestorClassificationOptions, getShareRegisterOptions, countriesData]
  )

  const companyInfoFields = getCompanyDetailsFields(data, helpers)
  const businessAddressFields = getBusinessAddressFields(data, helpers, false)
  const officeAddressFields = getOfficeAddressFields(data, helpers, false)

  const handleCheckboxChange = (key: string, value: any) => {
    setSameAsBusinessAddress(value)
    handleFieldChange?.(key, value)
  }

  const checkboxField = useMemo<CheckboxFieldConfig>(
    () => ({
      key: AccountRecordFields.accounts__same_as_business_address,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: sameAsBusinessAddress,
      checkboxLabel: 'Same as business address',
      editable: true
    }),
    [sameAsBusinessAddress]
  )

  return (
    <div
      data-debug='company-details-tab-container'
      data-component='DetailsTab'
      data-record-id={recordId}
      className='h-full'
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className='space-y-4'>
            <FormSection title='Company Details' titleColor='blue'>
              <div data-debug='company-details-content'>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={companyInfoFields}
                  onChange={(key, value) => handleFieldChange?.(key, value)}
                  enableAutoPatch={!!companyId}
                  patchId={companyId}
                  recordId={companyId}
                  onPatchSuccess={handlePatchSuccessLocal}
                  className='company-details-fields'
                  fieldWrapping={true}
                  currentRecord={currentCompany || data}
                />
              </div>
            </FormSection>

            {/* Reports print it for portfolio companies only. */}
            {showPortfolioSections && companyId ? <ShortDescriptionSection accountId={companyId} /> : null}

            {showPortfolioSections ? (
              <FundingRoundsTable
                companyData={currentCompany || formData || data}
                recordId={companyId}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={handlePatchSuccessLocal}
              />
            ) : null}
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <div className='space-y-4'>
            <FormSection title='Business Address' titleColor='blue'>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={businessAddressFields}
                onChange={(key, value) => handleFieldChange?.(key, value)}
                enableAutoPatch={!!companyId}
                patchId={companyId}
                recordId={companyId}
                onPatchSuccess={handlePatchSuccessLocal}
                className='business-address-fields'
                fieldWrapping={true}
              />
            </FormSection>

            <FormSection title='Registered Office' titleColor='blue'>
              <div>
                <div className='mb-3'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='inline'
                    fields={[checkboxField]}
                    onChange={handleCheckboxChange}
                    enableAutoPatch={!!companyId}
                    patchId={companyId}
                    recordId={companyId}
                    onPatchSuccess={handlePatchSuccessLocal}
                    className='registered-office-checkbox'
                  />
                </div>

                {!sameAsBusinessAddress && (
                  <InputManager
                    inputMode='read'
                    labelPosition='inline'
                    fields={officeAddressFields}
                    onChange={(key, value) => {
                      handleFieldChange?.(key, value)
                    }}
                    enableAutoPatch={!!companyId}
                    patchId={companyId}
                    recordId={companyId}
                    onPatchSuccess={handlePatchSuccessLocal}
                    className='office-address-fields'
                    fieldWrapping={true}
                  />
                )}
              </div>
            </FormSection>

            {/* Introduced by / Sourced by belong to the account: every deal and
                prospect form sends the user here to change them. */}
            <GeneralSection
              company={currentCompany}
              formData={formData}
              handleFieldChange={handleFieldChange}
              onPatchSuccess={onPatchSuccess}
              recordId={companyId}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}
